/**
 * What to say at the end of a run.
 *
 * Products' taunts are for a poor run — they are funny precisely because you
 * did badly. Aimed at someone who just played well they read as a slap, so a
 * good run gets praise instead (Ken, 2026-09-04).
 *
 * Two ways to earn the praise: clear the game's GOOD_SCORE, or beat your own
 * previous best. The second matters more than the first — someone whose best
 * is 40 and who just scored 60 has improved, and telling them the market left
 * them behind would be both wrong and discouraging.
 *
 * The praise lines are drafted in the same voice as products' taunts and
 * should go past products the same way theirs did.
 */

export const PRAISE = [
  "Chale, you dey try! 👏",
  "Massa, you sabi this game o! 🔥",
  "Ei! Sharp sharp!",
  "Chairman, we see you! 👑",
  "Woyɛ boss!",
  "Nice one — bring more.",
];

export function pickPraise(
  exclude?: string,
  random: () => number = Math.random,
): string {
  const pool =
    PRAISE.length > 1 && exclude
      ? PRAISE.filter((line) => line !== exclude)
      : PRAISE;
  return pool[Math.floor(random() * pool.length)];
}

export interface RunOutcome {
  score: number;
  /** The bar for this game — see each game's rules for how it was set. */
  goodScore: number;
  /** Their best BEFORE this run, so beating it can be recognised. */
  previousBest: number;
}

/** True when the run deserves praise rather than a taunt. */
export function runWentWell({
  score,
  goodScore,
  previousBest,
}: RunOutcome): boolean {
  if (score <= 0) return false;
  return score >= goodScore || score > previousBest;
}

/**
 * The line to show. Takes both pickers so each game keeps its own taunts —
 * a market taunt has no business appearing after a round of Kae Kente.
 */
export function endOfRunMessage(
  outcome: RunOutcome,
  pickers: {
    praise: (exclude?: string) => string;
    taunt: (exclude?: string) => string;
  },
  previousMessage?: string,
): string {
  return runWentWell(outcome)
    ? pickers.praise(previousMessage)
    : pickers.taunt(previousMessage);
}
